import React, { useState } from 'react';
import { GRADE_STYLES } from './gradeOverlay';
import { headline } from './GradeInspector';
import { noteName } from '../../utils/noteSpelling';

/**
 * The range graph: hovering over the highlighted bars after a graded take
 * shows the whole stretch at once. Each written note is a bar at its pitch
 * (coloured by its grade), the sung pitch runs across as dots, and the
 * background is divided where the grader divided the singing between the
 * notes: a solid line where a new word starts, a dashed one between notes
 * of the same word. The words run underneath. Hovering over a note says
 * how it was graded; clicking it plays it back.
 */

const PAD = { left: 48, right: 12, top: 26, bottom: 36 };
const CHART_HEIGHT = 240;
const MIN_WIDTH = 560;
const MAX_WIDTH = 1040;
const GAP = 8;
// The panel's padding either side, and its height beyond the chart
// (header, footer, legend), for placing it
const FRAME = 28;
const CHROME_HEIGHT = 120;
// Seconds shown either side of the range; semitones above and below the notes
const EDGE_SECONDS = 0.15;
const PITCH_MARGIN = 2;
// A note's name is written above it when its column is at least this wide
const NAME_MIN_PX = 30;
const DOT_RADIUS = 2.4;
// A written note's bar: this many semitones above and below its pitch
const BAR_HALF = 0.3;

const SUNG_COLORS = ['#cbd5e1', '#93c5fd', '#2563eb']; // neither, given to a note, graded
const BANDS = ['#f8fafc', '#eef2ff'];
const HOVER_BAND = '#dbeafe';

const syllable = (lyric) => (lyric ? `${lyric.text}${lyric.continues ? '-' : ''}` : '');

export default function RangeGraph({ range, onHold, canHear, hearing, onHearNote, playsSection, onPlaySection, onStop }) {
  const [hovered, setHovered] = useState(null);
  if (!range) return null;
  const { notes, trace, rect, bars, pass } = range;

  const width = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, window.innerWidth - 2 * GAP - FRAME));
  const height = CHART_HEIGHT + PAD.top + PAD.bottom;
  const panelHeight = height + CHROME_HEIGHT;
  const left = Math.max(GAP, Math.min((rect.left + rect.right) / 2 - (width + FRAME) / 2, window.innerWidth - width - FRAME - GAP));
  const below = rect.bottom + GAP + panelHeight <= window.innerHeight;
  const top = below ? rect.bottom + GAP : Math.max(GAP, rect.top - GAP - panelHeight);

  // Axes: score time across, pitch up
  const from = range.from - EDGE_SECONDS;
  const to = range.to + EDGE_SECONDS;
  const targets = notes.flatMap(n => n.detail.targets.map(t => t.midi));
  const low = Math.floor(Math.min(...targets) - PITCH_MARGIN);
  const high = Math.ceil(Math.max(...targets) + PITCH_MARGIN);
  const x = t => PAD.left + ((t - from) / (to - from)) * (width - PAD.left - PAD.right);
  const y = m => PAD.top + ((high - m) / (high - low)) * CHART_HEIGHT;
  const clampY = m => y(Math.max(low, Math.min(high, m)));

  // Where each note's column starts: where the grader began giving the
  // singing to it (its written start if nothing was given)
  const starts = notes.map(n => (n.span ? n.span.from : n.detail.start));
  const columns = notes.map((n, i) => ({
    from: i === 0 ? from : Math.max(starts[i - 1], Math.min(starts[i], to)),
    to: i === notes.length - 1 ? to : Math.max(starts[i], Math.min(starts[i + 1], to))
  }));
  const hasLyrics = notes.some(n => n.lyric);
  const fifths = notes[0].fifths;
  const pitchLines = [...new Set(notes.map(n => Math.round(n.detail.midi)))];
  const good = notes.filter(n => n.grade === 'good').length;
  const hover = hovered !== null ? notes[hovered] : null;

  return (
    <div
      className="dps-rangegraph"
      style={{ left, top, width: width + FRAME }}
      onMouseEnter={() => onHold(true)}
      onMouseLeave={() => { setHovered(null); onHold(false); }}
      role="dialog"
      aria-label="Graph of the highlighted bars"
    >
      <div className="dps-rangegraph-head">
        <strong>{bars ? bars[0].toUpperCase() + bars.slice(1) : 'Highlighted bars'}</strong>
        <span>pass {pass + 1} {'·'} {good} of {notes.length} in tune</span>
        {playsSection && (
          hearing === 'recording'
            ? <button type="button" onClick={onStop}>{'■'} Stop</button>
            : <button type="button" onClick={onPlaySection} disabled={!canHear}>{'▶'} Play my singing</button>
        )}
      </div>
      <svg width={width} height={height} className="dps-rangegraph-chart">
        {/* The singing as the grader divided it between the notes */}
        {columns.map((c, i) => (
          <rect key={`band${i}`} x={x(c.from)} y={PAD.top} width={Math.max(0, x(c.to) - x(c.from))} height={CHART_HEIGHT}
            fill={hovered === i ? HOVER_BAND : BANDS[i % 2]} />
        ))}
        {/* A line per semitone, named at the notes' pitches */}
        {Array.from({ length: high - low + 1 }, (_, k) => low + k).map(m => (
          <line key={`grid${m}`} x1={PAD.left} x2={width - PAD.right} y1={y(m)} y2={y(m)} stroke="#e2e8f0" strokeWidth="1" />
        ))}
        {pitchLines.map(m => (
          <text key={`pitch${m}`} x={PAD.left - 6} y={y(m) + 4} textAnchor="end" fontSize="12" fill="#475569">{noteName(m, fifths)}</text>
        ))}
        {/* Divisions: solid where a new word starts, dashed within a word */}
        {notes.slice(1).map((n, k) => {
          const newWord = !hasLyrics || (n.lyric && n.lyric.wordStart);
          return (
            <line key={`div${k}`} x1={x(starts[k + 1])} x2={x(starts[k + 1])} y1={PAD.top} y2={PAD.top + CHART_HEIGHT + 4}
              stroke={newWord ? '#475569' : '#94a3b8'} strokeWidth={newWord ? 1.4 : 1} strokeDasharray={newWord ? undefined : '3 3'} />
          );
        })}
        {/* The written notes at their targets, coloured by grade */}
        {notes.flatMap((n, i) => n.detail.targets.map((t, k) => {
          const style = GRADE_STYLES[n.grade] || GRADE_STYLES.missed;
          // (a note running past the range is cut at the chart's edge)
          const x1 = x(Math.max(t.from, n.detail.start, from));
          const x2 = x(Math.min(t.to, n.detail.end, to));
          return (
            <rect key={`note${i}-${k}`} x={x1} y={y(t.midi + BAR_HALF)} width={Math.max(2, x2 - x1)} height={y(t.midi - BAR_HALF) - y(t.midi + BAR_HALF)} rx="3"
              fill={n.grade === 'missed' ? 'none' : style.color} fillOpacity="0.28" stroke={style.color} strokeWidth="1.5"
              strokeDasharray={style.dashed ? '3 2' : undefined} />
          );
        }))}
        {/* The sung pitch */}
        {trace.filter(p => p.midi !== null).map(p => (
          <circle key={`s${p.time}`} cx={x(p.time)} cy={clampY(p.midi)} r={DOT_RADIUS} fill={SUNG_COLORS[p.role]}
            fillOpacity={p.midi < low || p.midi > high ? 0.4 : 1} />
        ))}
        {/* Names above, words below */}
        {notes.map((n, i) => {
          const middle = (x(columns[i].from) + x(columns[i].to)) / 2;
          const wide = x(columns[i].to) - x(columns[i].from) >= NAME_MIN_PX;
          return (
            <g key={`label${i}`}>
              {wide && <text x={middle} y={PAD.top - 8} textAnchor="middle" fontSize="12" fill="#475569">{noteName(n.detail.midi, n.fifths)}</text>}
              {n.lyric && (
                <text x={middle} y={PAD.top + CHART_HEIGHT + 24} textAnchor="middle" fontSize="15" fontWeight="600"
                  fill={n.grade === 'missed' ? '#94a3b8' : '#1e293b'}>{syllable(n.lyric)}</text>
              )}
            </g>
          );
        })}
        {/* Hover and click targets, one per note */}
        {columns.map((c, i) => (
          <rect key={`hit${i}`} x={x(c.from)} y={0} width={Math.max(0, x(c.to) - x(c.from))} height={height} fill="transparent"
            style={{ cursor: canHear && notes[i].detail.listen ? 'pointer' : 'default' }}
            onMouseEnter={() => setHovered(i)}
            onClick={() => { if (canHear && notes[i].detail.listen) onHearNote(notes[i]); }} />
        ))}
      </svg>
      <div className="dps-rangegraph-foot">
        {hover
          ? `${headline(hover, noteName(hover.detail.midi, hover.fifths))}${hover.lyric ? ` · “${hover.lyric.text}”` : ''}${canHear && hover.detail.listen ? ' · click to hear it' : ''}`
          : 'Hover over a note to see how it was graded; click it to hear it.'}
      </div>
      <div className="dps-rangegraph-legend">
        <span><i className="note" /> written note (colour: grade)</span>
        <span><i className="dot graded" /> graded singing</span>
        <span><i className="dot given" /> given to the note, not graded</span>
        <span><i className="line" /> {hasLyrics ? 'new word' : 'next note'}</span>
        {hasLyrics && <span><i className="line dashed" /> next note in the word</span>}
      </div>
    </div>
  );
}
